/**
 * Instrument publish wizard layout — Figma `5064:7413` / white card `4687:5493`.
 *
 * AppShell `main` already applies 32px gutter (`usesInstrumentPageGutter`).
 * All values below are **inside** the white wizard card unless noted.
 */

/**
 * Flow chrome above the white card — Figma `4687:5468`.
 * Title `4687:5470` (y=32, h=56) → breadcrumb `4687:5475` (y=116) → card `4687:5493` (y=164).
 */
export const PUBLISH_FLOW_TITLE_TO_SUBTITLE_GAP_PX = 4
/** Title block bottom → breadcrumb top: 116 − (32 + 56) = 28 */
export const PUBLISH_FLOW_TITLE_BLOCK_MARGIN_BOTTOM_PX = 28
/** Breadcrumb bottom → card top: 164 − (116 + 20) = 28 */
export const PUBLISH_BREADCRUMB_TO_CARD_GAP_PX = 28

/** White wizard card (`4687:5493`). */
export const PUBLISH_WIZARD_CARD_BORDER = '#e1e7f3'
export const PUBLISH_WIZARD_CARD_RADIUS_PX = 10
export const PUBLISH_WIZARD_CARD_PADDING_X_PX = 32
export const PUBLISH_WIZARD_CARD_PADDING_TOP_PX = 32
export const PUBLISH_WIZARD_CARD_PADDING_BOTTOM_PX = 32

/** Stepper rail (`4687:5494`) inside the card. */
export const PUBLISH_STEPPER_RAIL_BG = '#f2f6ff'
export const PUBLISH_STEPPER_RAIL_BORDER = '#e2e8f0'
export const PUBLISH_STEPPER_RAIL_RADIUS_PX = 15
export const PUBLISH_STEPPER_RAIL_MIN_HEIGHT_PX = 130
export const PUBLISH_STEPPER_RAIL_MARGIN_BOTTOM_PX = 44
/** Figma `5074:7443` — active step outer ellipse */
export const PUBLISH_STEPPER_ACTIVE_CIRCLE_SIZE_PX = 51
/** Figma `5074:7464` — inactive ellipse */
export const PUBLISH_STEPPER_INACTIVE_CIRCLE_SIZE_PX = 50
/** Figma — label top (283) minus circle bottom (262) */
export const PUBLISH_STEPPER_CIRCLE_TO_LABEL_GAP_PX = 21
/** Figma `5074:7435` — line starts ~2px after circle edge; keep minimal */
export const PUBLISH_STEPPER_CONNECTOR_INSET_PX = 0
/** Target Audience step body (`5064:7410`). */
export const PUBLISH_STEP_TITLE_MARGIN_BOTTOM_PX = 7
export const PUBLISH_STEP_SUBTITLE_TO_SUMMARY_PX = 32
export const PUBLISH_SUMMARY_TO_FILTER_GRID_PX = 44
/** Persona card `4687:5532` — 82px avatar; extra inset so text lines up with match card. */
export const PUBLISH_SUMMARY_PERSONA_ICON_TO_TEXT_GAP_PX = 34
/** Match card `4687:5542` — 98px icon. */
export const PUBLISH_SUMMARY_MATCH_ICON_TO_TEXT_GAP_PX = 18
/** Match card refresh hint — icon to “Updates as you adjust filters”. */
export const PUBLISH_MATCH_REFRESH_HINT_GAP_PX = 5
/** Figma filter grid `5074:7432` — row gap between stacked cards. */
export const PUBLISH_FILTER_GRID_ROW_GAP_PX = 24
/** Figma filter grid — column gap (543px cards + 33px gutter). */
export const PUBLISH_FILTER_GRID_COLUMN_GAP_PX = 33

/** Demographic filter section card — Figma `5310:9678` / `4687:6052`. */
export const PUBLISH_FILTER_SECTION_HEADER_BODY_GAP_PX = 12
export const PUBLISH_ACTIONS_MARGIN_TOP_PX = 32

/** Campaign Budget wizard — Figma `5332:9689` / `4687:6757` / `4687:6737` */
export const PUBLISH_BUDGET_BODY_MAX_WIDTH_PX = 780
export const PUBLISH_BUDGET_PANEL_PADDING_TOP_PX = 18
export const PUBLISH_BUDGET_PANEL_PADDING_X_PX = 24
export const PUBLISH_BUDGET_PANEL_PADDING_BOTTOM_PX = 24
/** Tab bar bottom → inner RewardCard top (`546 − 519`) */
export const PUBLISH_BUDGET_TAB_TO_BODY_GAP_PX = 27
/** Inner card top → title (`4687:6740` at y=24) */
export const PUBLISH_BUDGET_INNER_CARD_PADDING_TOP_PX = 24
export const PUBLISH_BUDGET_INNER_CARD_PADDING_X_PX = 24
/** Helper bottom → card bottom (`232 − 201.5`) */
export const PUBLISH_BUDGET_INNER_CARD_PADDING_BOTTOM_PX = 31
/** Response Target RewardCard — Figma `4687:6737` */
export const PUBLISH_BUDGET_RESPONSE_INNER_CARD_MIN_HEIGHT_PX = 232
/** Title block end → question (`83 − 48`) — Response Target only */
export const PUBLISH_BUDGET_TITLE_TO_QUESTION_GAP_PX = 35
/** Reward tab — Figma `4687:6841` */
export const PUBLISH_BUDGET_REWARD_SUBTITLE_TO_QUESTION_GAP_PX = 15
/** Question → number input (`116 − 105.5`) */
export const PUBLISH_BUDGET_QUESTION_TO_INPUT_GAP_PX = 11
/** Input bottom → helper (`182 − 173`) */
export const PUBLISH_BUDGET_INPUT_TO_HELPER_GAP_PX = 9
/** Number input horizontal inset — Figma `4687:6744` */
export const PUBLISH_BUDGET_INPUT_PADDING_X_PX = 12
/** Inner card bottom → tab Next (`800 − 778`) */
export const PUBLISH_BUDGET_INNER_TO_ACTION_GAP_PX = 22
/** Figma number field height */
export const PUBLISH_BUDGET_INPUT_HEIGHT_PX = 57

/** Campaign Budget tab — Figma `5336:9738` / `4687:6959` */
export const PUBLISH_BUDGET_BREAKDOWN_HEADER_PL_PX = 15
export const PUBLISH_BUDGET_BREAKDOWN_HEADER_PR_PX = 20
export const PUBLISH_BUDGET_BREAKDOWN_HEADER_HEIGHT_PX = 57
export const PUBLISH_BUDGET_BREAKDOWN_BODY_PADDING_PX = 24
export const PUBLISH_BUDGET_ESTIMATE_TO_ROWS_GAP_PX = 20
export const PUBLISH_BUDGET_BREAKDOWN_ROW_HEIGHT_PX = 44
export const PUBLISH_BUDGET_ROWS_TO_DIVIDER_GAP_PX = 8
export const PUBLISH_BUDGET_REMAINING_SECTION_PT_PX = 14
export const PUBLISH_BUDGET_REMAINING_SECTION_PB_PX = 10
export const PUBLISH_BUDGET_PROGRESS_TO_LABELS_GAP_PX = 8
export const PUBLISH_BUDGET_ESTIMATED_COST_MIN_HEIGHT_PX = 71
/** Status row cards — Figma `4687:7012` / `4687:7023` */
export const PUBLISH_BUDGET_STATUS_CARD_HEIGHT_PX = 50
export const PUBLISH_BUDGET_STATUS_CARD_PADDING_X_PX = 24
export const PUBLISH_BUDGET_STATUS_CARD_ICON_GAP_PX = 6
/** Between breakdown card and status cards (`1004 − 975.5` / `1083 − 1054`) */
export const PUBLISH_BUDGET_STATUS_CARD_GAP_PX = 29
/** Stacked campaign / wallet pot cards under Estimated Instrument Cost */
export const PUBLISH_BUDGET_POT_CARD_GAP_PX = 24
export const PUBLISH_BUDGET_POT_HEADER_HEIGHT_PX = 48
export const PUBLISH_BUDGET_POT_BODY_PT_PX = 8
export const PUBLISH_BUDGET_POT_BODY_PX_PX = 20
export const PUBLISH_BUDGET_POT_BODY_PB_PX = 20

/** Preview — Routing + Audience row (`5074:9521`); two equal columns, 33px gutter. */
export const PUBLISH_PREVIEW_SIDE_BY_SIDE_GAP_PX = PUBLISH_FILTER_GRID_COLUMN_GAP_PX
